import React, { useState, useEffect, useCallback } from 'react';
import { API, ax, btn, inp, card } from './adminHelpers';

export default function RolesTab() {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Create / Edit modal state
  const [modalMode, setModalMode] = useState(null); // 'create' | 'edit'
  const [selectedRole, setSelectedRole] = useState(null);
  const [formName, setFormName] = useState('');
  const [formDisplayName, setFormDisplayName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [selectedPermIds, setSelectedPermIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // User assignment state
  const [userSearch, setUserSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [selectedUserForRole, setSelectedUserForRole] = useState(null);
  const [assignRoleId, setAssignRoleId] = useState('');
  const [assigningRole, setAssigningRole] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [rolesRes, permsRes] = await Promise.all([
        ax().get(`${API}/api/admin/roles`),
        ax().get(`${API}/api/admin/permissions`),
      ]);
      setRoles(rolesRes.data.roles || []);
      setPermissions(permsRes.data.permissions || []);
    } catch (err) {
      console.error('[RolesTab] loadData error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openCreateModal = () => {
    setModalMode('create');
    setSelectedRole(null);
    setFormName('');
    setFormDisplayName('');
    setFormDescription('');
    setSelectedPermIds([]);
    setErrorMsg('');
  };

  const openEditModal = (role) => {
    setModalMode('edit');
    setSelectedRole(role);
    setFormName(role.name);
    setFormDisplayName(role.display_name);
    setFormDescription(role.description || '');
    setSelectedPermIds(role.permissions ? role.permissions.map((p) => p.id) : []);
    setErrorMsg('');
  };

  const togglePerm = (permId) => {
    setSelectedPermIds((prev) =>
      prev.includes(permId) ? prev.filter((id) => id !== permId) : [...prev, permId]
    );
  };

  const handleSaveRole = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrorMsg('');

    try {
      if (modalMode === 'create') {
        await ax().post(`${API}/api/admin/roles`, {
          name: formName,
          display_name: formDisplayName,
          description: formDescription,
          permission_ids: selectedPermIds,
        });
        setSuccessMsg('Role created successfully!');
      } else {
        await ax().put(`${API}/api/admin/roles/${selectedRole.id}`, {
          display_name: formDisplayName,
          description: formDescription,
          permission_ids: selectedPermIds,
        });
        setSuccessMsg('Role updated successfully!');
      }

      setModalMode(null);
      loadData();
      setTimeout(() => setSuccessMsg(''), 4000);
    } catch (err) {
      setErrorMsg(err.response?.data?.error || 'Failed to save role');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (!window.confirm('Are you sure you want to delete this custom role? Assigned users will revert to customer.')) {
      return;
    }
    try {
      await ax().delete(`${API}/api/admin/roles/${roleId}`);
      setSuccessMsg('Role deleted successfully');
      loadData();
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete role');
    }
  };

  // User search for role assignment
  const handleUserSearch = async (val) => {
    setUserSearch(val);
    if (!val || val.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearchingUsers(true);
    try {
      const res = await ax().get(`${API}/api/admin/customers`, { params: { search: val, limit: 8 } });
      setSearchResults(res.data.customers || []);
    } catch (_) {
      setSearchResults([]);
    } finally {
      setSearchingUsers(false);
    }
  };

  const handleAssignRole = async () => {
    if (!selectedUserForRole || !assignRoleId) return;
    setAssigningRole(true);
    try {
      await ax().post(`${API}/api/admin/users/${selectedUserForRole.id}/role`, {
        role_id: parseInt(assignRoleId),
      });
      setSuccessMsg(`Role successfully assigned to ${selectedUserForRole.name}!`);
      setSelectedUserForRole(null);
      setUserSearch('');
      setSearchResults([]);
      setAssignRoleId('');
      loadData();
      setTimeout(() => setSuccessMsg(''), 4000);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to assign role');
    } finally {
      setAssigningRole(false);
    }
  };

  // Group permissions by category
  const permsByCategory = permissions.reduce((acc, p) => {
    const cat = p.category || 'general';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(p);
    return acc;
  }, {});

  return (
    <div>
      {/* Top Title & Action */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20,
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#fff' }}>
            Role-Based Access Control (RBAC)
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#a0a0b8' }}>
            Configure system roles, fine-grained access permissions, and staff assignments
          </p>
        </div>

        <button
          onClick={openCreateModal}
          style={{
            ...btn('#9F5CFF'),
            padding: '10px 18px',
            fontSize: 13,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          ➕ Create Custom Role
        </button>
      </div>

      {successMsg && (
        <div
          style={{
            background: 'rgba(39,200,90,0.12)',
            border: '1px solid rgba(39,200,90,0.3)',
            color: '#27C85A',
            padding: '12px 18px',
            borderRadius: 10,
            marginBottom: 20,
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          ✓ {successMsg}
        </div>
      )}

      {/* Roles Grid */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#a0a0b8' }}>
          Loading roles and permissions…
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: 20,
            marginBottom: 32,
          }}
        >
          {roles.map((role) => {
            const isSystem = role.is_system;
            const roleColor =
              role.name === 'admin'
                ? '#9F5CFF'
                : role.name === 'event_organizer'
                ? '#00D2FF'
                : role.name === 'financial_manager'
                ? '#27C85A'
                : role.name === 'staff'
                ? '#f5a623'
                : '#E34364';

            return (
              <div
                key={role.id}
                style={{
                  ...card,
                  marginBottom: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  border: `1px solid ${roleColor}33`,
                  position: 'relative',
                }}
              >
                <div>
                  {/* Card Header: Role Name & System Badge */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: 8,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          background: roleColor,
                          boxShadow: `0 0 8px ${roleColor}`,
                        }}
                      />
                      <h3 style={{ margin: 0, fontSize: 16, color: '#fff', fontWeight: 700 }}>
                        {role.display_name}
                      </h3>
                    </div>

                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        padding: '3px 8px',
                        borderRadius: 12,
                        background: isSystem ? 'rgba(255,255,255,0.06)' : 'rgba(159,92,255,0.15)',
                        border: isSystem
                          ? '1px solid rgba(255,255,255,0.12)'
                          : '1px solid rgba(159,92,255,0.3)',
                        color: isSystem ? '#a0a0b8' : '#9F5CFF',
                      }}
                    >
                      {isSystem ? 'System Core' : 'Custom'}
                    </span>
                  </div>

                  <div style={{ fontFamily: 'monospace', fontSize: 11, color: roleColor, marginBottom: 8 }}>
                    slug: {role.name}
                  </div>

                  <p style={{ margin: '0 0 14px', fontSize: 12, color: '#a0a0b8', lineHeight: 1.4 }}>
                    {role.description || 'No description provided.'}
                  </p>

                  <div
                    style={{
                      display: 'inline-block',
                      background: 'rgba(255,255,255,0.04)',
                      padding: '4px 10px',
                      borderRadius: 6,
                      fontSize: 11,
                      color: '#fff',
                      marginBottom: 14,
                    }}
                  >
                    👥 <strong>{role.userCount || 0}</strong> assigned user(s)
                  </div>

                  {/* Permissions Pills */}
                  <div style={{ marginBottom: 16 }}>
                    <div
                      style={{
                        fontSize: 11,
                        color: '#7c7c9c',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        marginBottom: 6,
                      }}
                    >
                      Capabilities ({role.permissions ? role.permissions.length : 0})
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {role.name === 'admin' ? (
                        <span
                          style={{
                            background: 'rgba(159,92,255,0.15)',
                            color: '#9F5CFF',
                            fontSize: 11,
                            padding: '2px 8px',
                            borderRadius: 6,
                            fontWeight: 600,
                          }}
                        >
                          * Full Root Access (All Permissions)
                        </span>
                      ) : role.permissions && role.permissions.length > 0 ? (
                        role.permissions.map((p) => (
                          <span
                            key={p.id}
                            style={{
                              background: 'rgba(255,255,255,0.05)',
                              border: '1px solid rgba(255,255,255,0.08)',
                              color: '#fff',
                              fontSize: 10,
                              padding: '2px 6px',
                              borderRadius: 4,
                              fontFamily: 'monospace',
                            }}
                          >
                            {p.slug}
                          </span>
                        ))
                      ) : (
                        <span style={{ fontSize: 11, color: '#666', fontStyle: 'italic' }}>
                          Standard Client Access
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Card Actions */}
                <div
                  style={{
                    display: 'flex',
                    gap: 8,
                    paddingTop: 12,
                    borderTop: '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  <button
                    onClick={() => openEditModal(role)}
                    style={{
                      ...btn('#9F5CFF', true),
                      flex: 1,
                      fontSize: 11,
                      padding: '6px 12px',
                    }}
                  >
                    ✏️ Configure Perms
                  </button>
                  {!isSystem && (
                    <button
                      onClick={() => handleDeleteRole(role.id)}
                      style={{
                        ...btn('#E34364', true),
                        fontSize: 11,
                        padding: '6px 12px',
                      }}
                    >
                      🗑 Delete
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* User Role Quick Assignment Section */}
      <div style={{ ...card, padding: '24px' }}>
        <h3 style={{ margin: '0 0 6px', fontSize: 16, fontWeight: 700, color: '#fff' }}>
          Assign Role to User
        </h3>
        <p style={{ margin: '0 0 16px', fontSize: 13, color: '#a0a0b8' }}>
          Promote or assign a registered user (e.g. assigning an event organizer or staff member)
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
          {/* User Search Input */}
          <div style={{ position: 'relative' }}>
            <label style={{ display: 'block', fontSize: 12, color: '#a0a0b8', marginBottom: 6 }}>
              Find User by Name or Email:
            </label>
            <input
              type="text"
              placeholder="Search user..."
              value={userSearch}
              onChange={(e) => handleUserSearch(e.target.value)}
              style={inp(false)}
            />

            {/* Dropdown Results */}
            {searchResults.length > 0 && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  background: '#121832',
                  border: '1px solid rgba(159,92,255,0.3)',
                  borderRadius: 8,
                  zIndex: 100,
                  marginTop: 4,
                  maxHeight: 180,
                  overflowY: 'auto',
                }}
              >
                {searchResults.map((u) => (
                  <div
                    key={u.id}
                    onClick={() => {
                      setSelectedUserForRole(u);
                      setUserSearch(`${u.name} (${u.email})`);
                      setSearchResults([]);
                    }}
                    style={{
                      padding: '10px 14px',
                      cursor: 'pointer',
                      borderBottom: '1px solid rgba(255,255,255,0.05)',
                      fontSize: 13,
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(159,92,255,0.15)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <div style={{ color: '#fff', fontWeight: 600 }}>{u.name}</div>
                    <div style={{ fontSize: 11, color: '#a0a0b8' }}>{u.email}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Role Dropdown */}
          <div>
            <label style={{ display: 'block', fontSize: 12, color: '#a0a0b8', marginBottom: 6 }}>
              Select Role:
            </label>
            <select
              value={assignRoleId}
              onChange={(e) => setAssignRoleId(e.target.value)}
              style={{ ...inp(false), cursor: 'pointer' }}
            >
              <option value="">-- Choose a Role --</option>
              {roles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.display_name} ({r.name})
                </option>
              ))}
            </select>
          </div>

          {/* Submit Button */}
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button
              onClick={handleAssignRole}
              disabled={!selectedUserForRole || !assignRoleId || assigningRole}
              style={{
                ...btn('#27C85A'),
                width: '100%',
                padding: '11px',
                fontSize: 13,
                opacity: !selectedUserForRole || !assignRoleId || assigningRole ? 0.5 : 1,
              }}
            >
              {assigningRole ? 'Assigning…' : '✓ Update User Role'}
            </button>
          </div>
        </div>
      </div>

      {/* Create / Edit Role Modal */}
      {modalMode && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            zIndex: 10000,
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: 580,
              background: '#121832',
              borderRadius: 18,
              border: '1px solid rgba(159,92,255,0.3)',
              padding: 28,
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
          >
            <h3 style={{ margin: '0 0 16px', fontSize: 18, color: '#fff', fontWeight: 700 }}>
              {modalMode === 'create' ? '➕ Create Custom Role' : `Configure Permissions: ${selectedRole?.display_name}`}
            </h3>

            {errorMsg && (
              <div style={{ background: 'rgba(227,67,100,0.15)', color: '#E34364', padding: 10, borderRadius: 8, marginBottom: 14, fontSize: 13 }}>
                ⚠️ {errorMsg}
              </div>
            )}

            <form onSubmit={handleSaveRole}>
              {modalMode === 'create' && (
                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: 'block', fontSize: 12, color: '#a0a0b8', marginBottom: 4 }}>
                    Role Slug (e.g. "staff" or "floor_manager") *
                  </label>
                  <input
                    type="text"
                    required
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    placeholder="e.g. booth_staff"
                    style={inp(false)}
                  />
                </div>
              )}

              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 12, color: '#a0a0b8', marginBottom: 4 }}>
                  Display Name *
                </label>
                <input
                  type="text"
                  required
                  value={formDisplayName}
                  onChange={(e) => setFormDisplayName(e.target.value)}
                  placeholder="e.g. Field Staff Member"
                  style={inp(false)}
                />
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 12, color: '#a0a0b8', marginBottom: 4 }}>
                  Description
                </label>
                <textarea
                  rows={2}
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  placeholder="Summary of responsibilities and access level"
                  style={{ ...inp(false), resize: 'vertical' }}
                />
              </div>

              {/* Categorized Permissions Checkboxes */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#9F5CFF', marginBottom: 12, textTransform: 'uppercase' }}>
                  Select Permissions:
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {Object.entries(permsByCategory).map(([category, perms]) => (
                    <div key={category} style={{ background: 'rgba(255,255,255,0.02)', padding: 12, borderRadius: 10 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#a0a0b8', textTransform: 'uppercase', marginBottom: 8 }}>
                        {category}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8 }}>
                        {perms.map((p) => {
                          const isChecked = selectedPermIds.includes(p.id);
                          return (
                            <label
                              key={p.id}
                              style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 8,
                                fontSize: 12,
                                color: isChecked ? '#fff' : '#888',
                                cursor: 'pointer',
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => togglePerm(p.id)}
                                style={{ marginTop: 2, accentColor: '#9F5CFF' }}
                              />
                              <div>
                                <strong style={{ fontFamily: 'monospace', display: 'block' }}>{p.slug}</strong>
                                <span style={{ fontSize: 10, color: '#7c7c9c' }}>{p.description}</span>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: 12 }}>
                <button
                  type="button"
                  onClick={() => setModalMode(null)}
                  style={{ flex: 1, ...btn('rgba(255,255,255,0.1)', true), color: '#a0a0b8' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  style={{ flex: 2, ...btn('#9F5CFF') }}
                >
                  {saving ? 'Saving…' : '✓ Save Role & Permissions'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
